// Thin client for the Plus-side Find Talent proxy (/api/find-talent/*).
import { getAuthHeaders } from '@/hooks/useAuth';
import type { Brief, Candidate, FTSession, Role, SessionListItem } from './types';

async function j<T>(r: Response): Promise<T> {
  if (!r.ok) {
    const body = await r.json().catch(() => ({}));
    throw new Error(body?.error || `Request failed (${r.status})`);
  }
  return r.json();
}

export const ftApi = {
  loadSession: () =>
    fetch('/api/find-talent/session', { headers: getAuthHeaders() }).then(
      j<{ session: FTSession | null }>
    ),

  start: () =>
    fetch('/api/find-talent/session/start', { method: 'POST', headers: getAuthHeaders() }).then(
      j<FTSession>
    ),

  listSessions: () =>
    fetch('/api/find-talent/sessions', { headers: getAuthHeaders() }).then(
      j<{ sessions: SessionListItem[] }>
    ),

  loadSessionById: (id: string) =>
    fetch(`/api/find-talent/session/${encodeURIComponent(id)}`, { headers: getAuthHeaders() }).then(
      j<{ session: FTSession }>
    ),

  message: (id: string, text: string) =>
    fetch(`/api/find-talent/session/${encodeURIComponent(id)}/message`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ text }),
    }).then(j<{ message: string; brief: Brief | null; roles: Role[]; is_complete: boolean }>),

  setField: (id: string, key: string, value: string) =>
    fetch(`/api/find-talent/session/${encodeURIComponent(id)}/field`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ key, value }),
    }).then(j<{ brief: Brief | null; roles: Role[]; is_complete: boolean }>),

  uploadBrief: (id: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    // No Content-Type header — the browser sets the multipart boundary.
    const auth = getAuthHeaders() as Record<string, string>;
    delete auth['Content-Type'];
    return fetch(`/api/find-talent/session/${encodeURIComponent(id)}/brief-file`, {
      method: 'POST',
      headers: auth,
      body: form,
    }).then(j<{ message: string; brief: Brief | null; roles: Role[]; is_complete: boolean }>);
  },

  match: (id: string) =>
    fetch(`/api/find-talent/session/${encodeURIComponent(id)}/match`, {
      method: 'POST',
      headers: getAuthHeaders(),
    }).then(j<{ candidates: Candidate[] }>),

  createProject: (id: string, shortlist: string[] = []) =>
    fetch(`/api/find-talent/session/${encodeURIComponent(id)}/project`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ shortlist }),
    }).then(j<{ casting_project_id: string; moderation_status: string; public_url: string | null }>),

  status: (id: string) =>
    fetch(`/api/find-talent/session/${encodeURIComponent(id)}/status`, {
      headers: getAuthHeaders(),
    }).then(
      j<{
        casting_project_id: string | null;
        moderation_status: FTSession['moderation_status'];
        moderation_note: string | null;
        public_url: string | null;
        candidate_status: FTSession['candidate_status'];
      }>
    ),

  requestTape: (id: string, candidateId: string) =>
    fetch(
      `/api/find-talent/session/${encodeURIComponent(id)}/candidates/${encodeURIComponent(
        candidateId
      )}/request`,
      { method: 'POST', headers: getAuthHeaders() }
    ).then(j<{ candidate_id: string; status: string }>),

  tape: (id: string, candidateId: string) =>
    fetch(
      `/api/find-talent/session/${encodeURIComponent(id)}/candidates/${encodeURIComponent(
        candidateId
      )}/tape`,
      { headers: getAuthHeaders() }
    ).then(j<{ tape_url: string | null; status: string }>),

  // DEV-ONLY: flip the mock project to approved so the flow is walkable.
  devApprove: (id: string) =>
    fetch('/api/find-talent/dev/approve', {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ session_id: id }),
    }).then(j<{ session_id: string; moderation_status: string }>),
};
